// src/chatAdmin/index.js — 💬 Messages (Phase A4): the managers-app chat view.
//
// The account side of the WhatsApp replacement: an AM sees their area + its
// stores + company; HEAD OFFICE sees EVERY channel (the oversight surface the
// WhatsApp ban is traded against — the staff app says "Visible to Head Office",
// this is where that promise is real). Same ELC-Jack/WhatsApp skin as the staff
// tab. Auth = the admin Bearer (DB.getToken) — no PINs here; the Worker
// attributes account sends from the session.
//
// HO extra: a 🗑 on every bubble → POST /api/chat/delete (SOFT delete — the
// bubble becomes "removed", the row is retained for audit).

function esc(s) {
  return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
// v132.85 (overnight audit): the ONE safe way to put text inside
// a quoted inline-handler argument. JS-escape first (backslash, quote, new line), THEN
// HTML-escape. esc() alone turns ' into &#39;, which the browser decodes
// back to a quote before the handler runs, so a crafted name broke out.
function jsArg(s) {
  return esc(String(s == null ? '' : s).replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/\r?\n/g, '\\n'));
}
// SECURITY (v125.14): blank any photo src that isn't a clean base64 image
// data-URL before it hits innerHTML. This is the ADMIN origin (Bearer token in
// localStorage), so the stored-XSS class is closed here belt-and-braces on top
// of the server validator (validatePhoto). A staff-injected photo can never
// execute markup in the console.
// v129.121 — 🎤 voice notes. v129.119 shipped them to the STAFF chat bundle
// only, and Head Office chat is a SEPARATE implementation — so the HO view had
// no microphone at all. The recorder now lives in src/shared/voice-note.js and
// both apps import it.
//
// NOTE: this file had NO imports before now, which is exactly the setup that
// nearly shipped an undefined global in v129.112. A test asserts the BUILT
// dist/chatAdmin.js carries the shared code.
import { startRecording, stopRecording, cancelRecording, playVoiceInto, voiceStubHtml, fmtDur as vnFmtDur, voiceSupported } from '../shared/voice-note.js';
import { decodeDataUrl, openStoredFile } from '../shared/open-file.js';

function okPhoto(s) {
  return /^data:image\/(jpeg|jpg|png|webp);base64,[A-Za-z0-9+/]+={0,2}$/.test(String(s == null ? '' : s)) ? String(s) : '';
}
function api(path, opts) {
  opts = opts || {};
  var base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
  var headers = { 'Content-Type': 'application/json' };
  var tk = (typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : null;
  if (tk) headers['Authorization'] = 'Bearer ' + tk;
  return fetch(base + path, { method: opts.method || 'GET', headers: headers, body: opts.body, cache: 'no-store' });
}

var _channels = [];
var _activeKey = null;
var _activeTitle = '';
var _pendingDm = null;
var _pollTimer = null;
var _listTick = 0;      // v129.133 — the list refreshes every 3rd 6s tick
var _wakeBound = false; // v129.133 — foreground wake bound once
var _isHO = false;
var _photoCache = {};            // message id → data URL (A6)
// v125.52 (Matt: "flag questions staff are asking, any not in english and
// anything that might be a concern") — the read-only oversight triage.
var _flags = null;               // [{ id, channel_key, channel_title, sender_name, sender_store, body, created_at, flags, concern_terms }]
var _flagCounts = null;          // { question, nonEnglish, concern, total }
var _flagDays = 14;              // v125.87 — the scan window the Worker reported
var _flagScanned = null;         // v125.87 — staff messages the scan looked at
var _flagsOpen = false;
var _pickOpen = false;            // v125.63: the new-conversation picker is open
var _chatFilter = '';             // v125.92: the conversation-list search text (HO sees EVERY channel)
var _pickPeople = [];             // v125.65: cached picker data, so search filters live
var _pickStores = [];
var _sumOpen = false;             // v125.70: the AI-summary panel is open
var _sumText = null;              // last summary text (kept until refreshed)
var _sumStats = null;
var _sumBusy = false;
var _sumErr = null;

function root() { return document.getElementById('chatAdminRoot'); }

// v129.140 (the Qatar area manager, 14 Aug: "trying to reply to messages but
// unable to view the reply space" — Windows Edge, Admin app).
//
// MEASURED, not guessed. `.wac-shell` sizes itself `calc(100dvh - 190px)` with
// a `min-height:440px` floor — a hard-coded guess at admin.html's chrome, and
// the comment on that rule has said so since v129.25. Probing the real
// admin.html in Chromium: the space above #chatAdminRoot is 168px at >=1280px
// wide, but **210px** once the tab bar wraps (roughly 901-1279px). So in that
// whole band the shell is already 20px too tall, and on a short window the
// 440px floor adds more. Composer bottom vs viewport bottom, measured:
//   950x600 -> +58px below   1000x560 -> +98px   1093x500 -> +158px
//   1100x650 -> +28px        1280x600 -> +16px
//
// AND IT IS NOT SCROLLABLE-TO IN PRACTICE. The document does scroll, but the
// pointer is over `.wac-msgs` (the person is reading the thread), which is
// `overflow-y:auto; overscroll-behavior:contain` — once that pane hits its end
// the wheel does NOT chain to the page. So the composer sits below the fold
// and the obvious gesture cannot reach it: "cannot see the reply space".
//
// The <=900px branch was rescued in v129.25 with position:fixed. Desktop never
// was. Rather than swap 190 for 210 — the same guess, wrong again the next time
// the chrome changes — measure the shell's own top and end it at the viewport
// bottom. Runs only above the 900px breakpoint; the fixed branch owns the rest,
// and chat.html's `height:100% !important` override still wins there.
var _fitBound = false;
function fitShellHeight() {
  try {
    var el = root(); if (!el) return;
    var shell = el.querySelector('.wac-shell'); if (!shell) return;
    if (window.innerWidth <= 900) { shell.style.height = ''; shell.style.minHeight = ''; return; }
    var top = shell.getBoundingClientRect().top + (window.scrollY || 0);
    var avail = window.innerHeight - top - 12;         // 12px breathing room
    if (!(avail > 0)) return;                          // mid-scroll/hidden — leave the CSS alone
    shell.style.height = Math.max(260, Math.round(avail)) + 'px';
    shell.style.minHeight = '0px';                     // the 440px floor is what overhangs
  } catch (e) { /* CSS fallback stands */ }
}
function bindFit() {
  if (_fitBound || typeof window === 'undefined') return;
  _fitBound = true;
  window.addEventListener('resize', fitShellHeight);
}
function initials(name) {
  return String(name || '?').split(/\s+/).filter(Boolean).map(function (w) { return w[0]; }).slice(0, 2).join('').toUpperCase() || '?';
}
function fmtTime(iso) {
  var d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
}

var WALLPAPER = 'url("data:image/svg+xml,%3Csvg width=\'40\' height=\'40\' viewBox=\'0 0 40 40\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23000\'%3E%3Ccircle cx=\'10\' cy=\'10\' r=\'1.5\'/%3E%3Ccircle cx=\'30\' cy=\'10\' r=\'1.5\'/%3E%3Ccircle cx=\'10\' cy=\'30\' r=\'1.5\'/%3E%3Ccircle cx=\'30\' cy=\'30\' r=\'1.5\'/%3E%3Ccircle cx=\'20\' cy=\'20\' r=\'1.5\'/%3E%3C/g%3E%3C/svg%3E")';
function ensureStyles() {
  if (document.getElementById('waAdminChatStyles')) return;
  var css = '' +
    '.wac-shell{display:flex;flex-direction:column;height:calc(100dvh - 190px);min-height:440px;max-width:640px;background:var(--adm-chat-wall);border-radius:12px;overflow:hidden;position:relative;box-shadow:0 1px 6px rgba(0,0,0,.12)}' +
    // v125.94 — pointer-events:none is LOAD-BEARING (same wallpaper-eats-clicks
    // bug as the staff app: an absolutely-positioned layer hit-tests above all
    // static content, so any un-lifted row was click-dead). See src/chat.
    '.wac-wp{position:absolute;inset:0;z-index:0;opacity:.04;pointer-events:none;background-image:' + WALLPAPER + '}' +
    // v136 dark mode: the green head, its white avatar disc and the green unread, send and new-chat buttons are
    // brand fills and keep their colours in both themes. Everything else reads the --adm-chat- variables in
    // shift-ui.css (the admin comms block), so admin.html and chat.html look the same as before in Light.
    '.wac-head{display:flex;align-items:center;gap:10px;padding:10px 14px;background:#075e54;z-index:10;flex-shrink:0;color:#fff;box-shadow:0 1px 4px rgba(0,0,0,.35)}' +
    '.wac-back{background:none;border:none;color:#fff;font-size:20px;cursor:pointer;padding:2px 6px}' +
    '.wac-av{width:38px;height:38px;border-radius:50%;background:#fff;color:#075e54;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:14px;flex-shrink:0}' +
    '.wac-hname{font-weight:600;font-size:15px}.wac-hsub{font-size:11px;color:#b2dfdb}' +
    '.wac-list{flex:1;overflow-y:auto;z-index:1;background:var(--adm-chat-card)}' +
    '.wac-item{display:flex;align-items:center;gap:12px;padding:10px 14px;border-bottom:1px solid var(--adm-chat-line);cursor:pointer;background:var(--adm-chat-card)}' +
    '.wac-item:hover{background:var(--adm-chat-hover)}' +
    '.wac-iav{width:40px;height:40px;border-radius:50%;background:var(--adm-chat-avatar);color:var(--adm-chat-sub);display:flex;align-items:center;justify-content:center;font-weight:700;font-size:14px;flex-shrink:0}' +
    '.wac-ititle{font-size:14px;font-weight:600;color:var(--adm-chat-text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}' +
    '.wac-ipreview{font-size:12px;color:var(--adm-chat-meta);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}' +
    '.wac-iunread{min-width:20px;height:20px;border-radius:10px;background:#25d366;color:#fff;font-size:11px;font-weight:700;display:flex;align-items:center;justify-content:center;padding:0 6px}' +
    '.wac-msgs{flex:1;overflow-y:auto;overscroll-behavior:contain;padding:12px 10px;z-index:1;display:flex;flex-direction:column;gap:4px}' +
    // v129.25 (Matt, 3 Aug: staff "lose the back button" scrolling chat on
    // mobile) — the ADMIN Messages thread. The staff app got a full-screen
    // phone thread in v127.49; chatAdmin never did. Its shell is a normal-flow
    // block whose height is a hard-coded guess at the surrounding chrome
    // (100dvh - 190px) with a 440px floor. On a short phone the floor wins, the
    // shell is taller than the space, the DOCUMENT scrolls, and the whole card
    // — header, back arrow and all — slides up under the sticky topbar.
    //
    // position:sticky on .wac-head would NOT fix it: .wac-shell has
    // overflow:hidden, so the header's scrollport is a shell that is itself
    // scrolling off the page. The shell has to leave normal flow.
    //
    // 900px, not v127.49's 700px: chatAdmin has no two-pane wide layout to
    // protect, and 701-899px (phone landscape, small tablets) fails the same
    // arithmetic today.
    // v129.125 (Matt, 13 Aug, with a photo: "on iphone its behind the carrier
    // signal info"). TWO faults in this one rule, both from the shell becoming
    // position:fixed:
    //
    // 1. THE TOP SAFE AREA WAS NEVER PADDED. The bottom was — that is the
    //    .wac-input line below, added when the shell went fixed — and the top
    //    was missed in the same edit. Fixed to inset:0, this header IS the top
    //    of the screen (it covers chat.html's own branded bar), so on a notched
    //    iPhone the status bar draws straight over it: the clock lands on the
    //    avatar and the carrier/LTE/battery cluster sits exactly where the 📹
    //    and 🔔 are. The buttons were rendering all along — they were
    //    underneath iOS. Nothing about "the button is missing" was a code path.
    //
    // 2. OVER-CONSTRAINED HEIGHT, which is very likely Matt's other report
    //    ("it only loads after you type your first message"). With top:0,
    //    bottom:0 AND height all set, CSS drops `bottom` and the box becomes
    //    top:0 + 100dvh. When 100dvh is taller than what is actually visible —
    //    routine on iOS during a standalone launch, and whenever the keyboard
    //    state changes — the shell overhangs the screen, so the messages pane
    //    and composer sit below the fold and the thread reads as "not loaded".
    //    Focusing the textarea opens the keyboard, dvh recomputes, and it snaps
    //    into place: hence "only after you type". inset:0 alone already means
    //    exactly the viewport, so the explicit height is removed rather than
    //    swapped for another unit.
    '@media (max-width:900px){' +
      '.wac-full{position:fixed;inset:0;min-height:0;max-width:none;margin:0;border-radius:0;z-index:6000}' +
      '.wac-full .wac-head{padding-top:calc(10px + env(safe-area-inset-top,0px))}' +
      '.wac-full .wac-input{padding-bottom:calc(10px + env(safe-area-inset-bottom,0px))}}' +
    '.wac-datechip{text-align:center;margin:4px 0 10px}.wac-datechip span{font-size:11px;background:var(--adm-chat-chip);color:var(--adm-chat-sub);padding:4px 14px;border-radius:8px}' +
    '.wac-row{display:flex;justify-content:flex-start;margin-bottom:2px}.wac-row.me{justify-content:flex-end}' +
    '.wac-bubble{max-width:80%;min-width:80px;border-radius:8px;padding:7px 10px 5px;box-shadow:0 1px 2px rgba(0,0,0,.12);background:var(--adm-chat-card);border-top-left-radius:0;position:relative}' +
    '.wac-row.me .wac-bubble{background:var(--adm-chat-me);border-top-left-radius:8px;border-top-right-radius:0}' +
    // When HO OBSERVES a 1-1 conversation it is not part of (nothing is "mine"),
    // the two participants are split like WhatsApp: one right + green, one left +
    // white (the ops manager's request). Keeps the sender name (unlike .me) so HO can
    // still tell who is who.
    '.wac-row.rhs{justify-content:flex-end}' +
    '.wac-row.rhs .wac-bubble{background:var(--adm-chat-me);border-top-left-radius:8px;border-top-right-radius:0}' +
    '.wac-reacts{display:flex;gap:4px;margin-top:3px;flex-wrap:wrap}' +                             // v125.86 👍
    '.wac-react{font-size:11.5px;background:var(--adm-chat-card);border:1px solid var(--adm-chat-line-mid);border-radius:10px;padding:1px 7px;cursor:pointer;box-shadow:0 1px 1px rgba(0,0,0,.08);user-select:none}' +
    '.wac-react.mine{background:var(--adm-chat-react-mine);border-color:var(--brand,#009E3D);font-weight:700}' +
    '.wac-bname{font-size:12px;font-weight:700;color:var(--adm-chat-accent);margin-bottom:3px}' +
    '.wac-btext{font-size:14px;line-height:1.5;color:var(--adm-chat-text);word-break:break-word;white-space:pre-wrap}' +
    '.wac-btime{font-size:10px;color:var(--adm-chat-meta);text-align:end;margin-top:2px}' +
    '.wac-del{font-size:13px;color:var(--adm-chat-faint);font-style:italic}' +
    '.wac-zap{position:absolute;top:2px;right:4px;background:none;border:none;cursor:pointer;font-size:11px;opacity:.35}' +
    // v125.72 — quote-reply: the quoted original + the composer reply bar
    '.wac-quote{border-inline-start:3px solid #25d366;background:var(--adm-chat-quote);border-radius:6px;padding:4px 8px;margin:2px 0 5px;font-size:12px;color:var(--adm-chat-sub);overflow:hidden}' +
    '.wac-quote b{display:block;color:var(--adm-chat-accent);font-size:11.5px}' +
    '.wac-quote span{display:block;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}' +
    '.wac-pendbar{display:flex;align-items:center;gap:8px;padding:6px 12px;background:var(--adm-chat-panel);border-top:1px solid var(--adm-chat-edge)}' +
    '.wac-pendbar img{width:34px;height:34px;object-fit:cover;border-radius:6px;flex-shrink:0}' +
    '.wac-pendx{border:none;background:none;font-size:15px;cursor:pointer;color:var(--adm-chat-sub);flex-shrink:0}' +
    '.wac-zap:hover{opacity:1}' +
    '.wac-input{display:flex;align-items:flex-end;gap:6px;padding:8px 10px 10px;background:var(--adm-chat-panel);z-index:10;flex-shrink:0;border-top:1px solid var(--adm-chat-edge)}' +
    '.wac-text{flex:1;border:none;border-radius:20px;padding:11px 14px;font-size:14px;font-family:inherit;resize:none;max-height:90px;background:var(--input-bg);outline:none}' +
    '.wac-send{width:44px;height:44px;border-radius:50%;border:none;background:#25d366;color:#fff;cursor:pointer;font-size:18px;flex-shrink:0;box-shadow:0 2px 5px rgba(0,0,0,.2)}' +
    // v125.55 — emoji picker (Matt: "can there [be] new emojis in Chat")
    '.wac-emoji{width:38px;height:38px;border:none;background:none;font-size:22px;cursor:pointer;flex-shrink:0;padding:0}' +
    '.wac-emoji-pop{position:absolute;bottom:64px;left:8px;right:8px;background:var(--adm-chat-card);border:1px solid var(--adm-chat-edge);border-radius:12px;padding:8px;display:flex;flex-wrap:wrap;gap:2px;max-height:190px;overflow-y:auto;z-index:40;box-shadow:0 4px 18px rgba(0,0,0,.2)}' +
    '.wac-emoji-pop button{border:none;background:none;font-size:22px;cursor:pointer;width:38px;height:38px;border-radius:8px;padding:0}' +
    '.wac-emoji-pop button:hover{background:var(--adm-chat-panel)}' +
    // v125.63 — new-conversation FAB
    '.wac-fab{position:absolute;right:16px;bottom:16px;width:52px;height:52px;border-radius:50%;background:#075e54;color:#fff;border:none;font-size:22px;cursor:pointer;box-shadow:0 3px 8px rgba(0,0,0,.3);z-index:20}' +
    // v125.65 — search box in the new-conversation picker
    '.wac-search{margin:8px 10px;padding:9px 12px;border:1px solid var(--adm-chat-edge);border-radius:20px;font-size:14px;outline:none;width:calc(100% - 20px);box-sizing:border-box;background:var(--adm-chat-panel)}' +
    '.wac-search:focus{border-color:#075e54;background:var(--input-bg)}' +
    // v125.70 — the ✨ AI-summary button (HO header) + panel body
    '.wac-ai{margin-inline-start:auto;background:rgba(255,255,255,.16);color:#fff;border:none;border-radius:16px;padding:7px 13px;font-size:12.5px;cursor:pointer;flex-shrink:0}' +
    '.wac-ai:hover{background:rgba(255,255,255,.28)}' +
    '.wac-sum{padding:16px 18px;font-size:14px;line-height:1.55;color:var(--adm-chat-text);white-space:pre-wrap;background:var(--adm-chat-card);margin:10px;border-radius:10px;box-shadow:0 1px 2px rgba(0,0,0,.08)}' +
    '.wac-fbtn{background:#075e54;color:#fff;border:none;border-radius:16px;padding:8px 16px;font-size:12.5px;cursor:pointer}' +
    '.wac-photo{margin:2px 0 4px}.wac-photo img{max-width:100%;max-height:220px;border-radius:8px;cursor:pointer;display:block}' +
    '.wac-photo-ph{width:160px;height:100px;border-radius:8px;background:var(--adm-chat-panel);color:var(--adm-chat-sub);font-size:12px;display:flex;align-items:center;justify-content:center}' +
    '.wac-viewer{position:fixed;inset:0;background:rgba(0,0,0,.88);z-index:9999;display:flex;align-items:center;justify-content:center;cursor:zoom-out}' +
    '.wac-viewer img{max-width:96vw;max-height:92vh;border-radius:6px}' +
    // v125.52 — the "Needs attention" banner + flags panel
    '.wac-flagbar{display:flex;align-items:center;gap:10px;padding:10px 12px;margin:8px;background:var(--adm-chat-flag-bg);border:1px solid var(--adm-chat-flag-line);border-radius:10px;cursor:pointer;z-index:1;position:relative}' +
    '.wac-flagpills{display:flex;gap:6px}' +
    '.wac-fp{font-size:11px;font-weight:800;border-radius:9px;padding:2px 8px;white-space:nowrap}' +
    '.wac-fp-concern{background:var(--adm-chat-concern-bg);color:var(--adm-chat-concern-text)}' +
    '.wac-fp-q{background:var(--adm-chat-q-bg);color:var(--adm-chat-q-text)}' +
    '.wac-fp-lang{background:var(--adm-chat-lang-bg);color:var(--adm-chat-lang-text)}' +
    '.wac-flag-note{font-size:11.5px;color:var(--adm-chat-note-text);background:var(--adm-chat-note-bg);padding:8px 12px;z-index:1;position:relative;line-height:1.5}' +
    '.wac-flag-item{background:var(--adm-chat-card);border-bottom:1px solid var(--adm-chat-line);padding:11px 14px;cursor:pointer;z-index:1;position:relative}' +
    '.wac-flag-item:hover{background:var(--surface-2)}' +
    '.wac-flag-top{display:flex;justify-content:space-between;gap:8px;align-items:baseline}' +
    '.wac-flag-who{font-weight:700;font-size:13px;color:var(--adm-chat-text)}' +
    '.wac-flag-when{font-size:11px;color:var(--adm-chat-faint);white-space:nowrap}' +
    '.wac-flag-body{font-size:13px;color:var(--adm-chat-ink-2);margin:4px 0 6px;word-break:break-word}' +
    '.wac-flag-tags{display:flex;align-items:center;gap:6px;flex-wrap:wrap}' +
    '.wac-flag-chan{margin-inline-start:auto;font-size:11px;color:var(--adm-chat-accent);font-weight:700}' +
    '.wac-flag-terms{font-size:11px;color:var(--adm-chat-concern-text);margin-top:4px}';
  var el = document.createElement('style');
  el.id = 'waAdminChatStyles';
  el.textContent = css;
  document.head.appendChild(el);
}

// Entry point — called by switchTab('messages').
async function render() {
  ensureStyles();
  bindPaste();        // v125.100 — Ctrl+V a screenshot into the composer
  bindFit();          // v129.140 — keep the shell inside the viewport on resize
  var el = root(); if (!el) return;
  if (_flagsOpen) { renderFlags(); }
  else if (_sumOpen) { renderSummary(); }
  else if (_pickOpen) { openNewChat(); }
  else if (_activeKey || _pendingDm) { renderThread(); } else { renderList(); }
  // v129.140 — every branch above replaces innerHTML, so the measurement has to
  // happen AFTER the paint, on whichever shell it just built. renderThread is
  // async (it awaits refreshThread), so defer past this tick rather than
  // measuring a shell that is about to be re-laid-out.
  setTimeout(fitShellHeight, 0);
  // v129.133 — WHY THIS CHANGED (Matt: "It loads slowly… says loading until you
  // type a message. It also doesn't show new messages until you type").
  //
  // Every symptom was one fact: the only thing that refreshed a thread promptly
  // was SENDING (send() calls refreshThread(true) directly). Everything else
  // waited up to 15 seconds — including the first paint after opening a chat,
  // and including a message someone else had already sent. On a phone that
  // reads as "broken until I type", because it is indistinguishable from it.
  //
  // Three changes, and the request budget stays flat — the v129.93 lesson (the
  // ring poll was the single biggest request stream) is not being un-learned:
  //   * an OPEN THREAD polls at 6s, but ONLY while the app is actually visible.
  //     A thread open on a visible screen is a small slice of the day, and it is
  //     the only moment liveness is felt.
  //   * the LIST stays at 15s — a preview being seconds stale costs nothing.
  //   * refreshFlags drops OFF the tick entirely and runs at most every 5
  //     minutes. It is a 14-day oversight scan that was firing every 15s on
  //     every phone; that single change more than pays for the faster thread
  //     poll (4/min saved on the list view vs 6/min added on an open thread).
  //
  // Plus the thing no interval can do: refresh IMMEDIATELY when the app comes
  // back to the foreground. Picking your phone up and seeing yesterday's view
  // for another 6 seconds is most of what "slow" meant.
  if (!_pollTimer) {
    _pollTimer = setInterval(function () {
      var pane = root();
      if (!pane || pane.offsetParent === null || document.hidden) return; // tab not visible
      if (_activeKey) { refreshThread(); return; }
      if (_pendingDm) return;
      // v133.44: while "Needs attention" is open, keep it: only its own (5-minute) refresh runs
      if (_flagsOpen) { maybeRefreshFlags(); return; }
      _listTick = (_listTick + 1) % 3;                 // 6s ticks -> the list every ~18s
      if (_listTick === 0) refreshChannels();
      maybeRefreshFlags();
    }, 6000);
  }
  if (!_wakeBound && typeof document !== 'undefined') {
    _wakeBound = true;
    // Both events: visibilitychange covers tab/app switching, focus covers a
    // window regaining focus without a visibility transition (desktop).
    var wake = function () {
      if (document.hidden) return;
      var pane = root();
      if (!pane || pane.offsetParent === null) return;
      if (_activeKey) refreshThread(); else if (_flagsOpen) maybeRefreshFlags(); else if (!_pendingDm) refreshChannels();
    };
    document.addEventListener('visibilitychange', wake);
    try { window.addEventListener('focus', wake); } catch (e) { /* no window — tests */ }
  }
  refreshChannels();
  maybeRefreshFlags();
}

// v129.133 — the oversight scan is expensive and slow-moving; 5 minutes is far
// more often than a 14-day flag window can meaningfully change.
var _flagsAt = 0;
function maybeRefreshFlags() {
  var now = Date.now();
  if (now - _flagsAt < 5 * 60 * 1000) return;
  _flagsAt = now;
  return refreshFlags();
}

// v125.52 — the oversight triage. Account Bearer; the Worker scopes an AM to
// their area. Silently no-ops on error (older Worker, offline) — the banner
// just doesn't appear.
async function refreshFlags() {
  try {
    var res = await api('/api/chat/flags');
    if (!res.ok) return;
    var d = await res.json();
    if (d && d.ok) {
      _flags = d.flags || [];
      _flagCounts = d.counts || null;
      _flagDays = d.days || 14;                                    // v125.87
      _flagScanned = d.scanned != null ? d.scanned : null;         // v125.87
      var el = root();
      if (el && el.offsetParent !== null) {
        if (_flagsOpen) renderFlags();
        else if (!_activeKey && !_pendingDm && !_pickOpen && !_sumOpen) renderList();
      }
    }
  } catch (e) { /* next poll */ }
}

async function refreshChannels() {
  try {
    // v125.62 — the /chat app sets window.__chatPersonal so HO sees a PERSONAL
    // list (their DMs + company) there; the admin Messages tab leaves it unset
    // and keeps the full oversight list.
    var scope = (typeof window !== 'undefined' && window.__chatPersonal) ? '?scope=personal' : '';
    var res = await api('/api/chat/channels' + scope);
    if (!res.ok) return;
    var d = await res.json();
    if (d && d.ok) {
      _channels = d.channels || [];
      // v125.101 (Matt: "initially it is green, then once sent goes white") —
      // sessions saved before the login response carried the account id have
      // id:null, so myAccountId() never matched sender_id and OWN bubbles lost
      // their "mine" styling after the optimistic echo. The channels route now
      // names the viewer; heal the in-memory session from it.
      try {
        if (d.viewer && d.viewer.kind === 'account' && d.viewer.id != null &&
            typeof currentAdmin !== 'undefined' && currentAdmin && currentAdmin.id == null) {
          currentAdmin.id = Number(d.viewer.id);
        }
      } catch (e2) { /* never break the poll */ }
      var el = root();
      // v125.92: never repaint while someone is typing in the search box (the
      // staff app's v125.47 guard — a poll repaint mid-word eats the keystroke).
      var searching = document.activeElement && document.activeElement.id === 'wacChatSearch';
      // v133.44 (Matt, 5 Oct 2026: "needs attention isnt stable. it often crashes and kicks me out"): the
      // flags panel was the one open view missing from this guard, so the list was painted over it.
      if (el && !_activeKey && !_pendingDm && !_pickOpen && !_sumOpen && !_flagsOpen && !searching && el.offsetParent !== null) renderList();
    }
  } catch (e) { /* next poll */ }
}

function renderList() {
  var el = root(); if (!el) return;
  _isHO = (typeof currentAdmin !== 'undefined' && currentAdmin && currentAdmin.role === 'admin');
  // v125.92 (Head Office: "in chat the search function is not working") — the admin
  // list never HAD one (only the new-chat picker searched, and that only finds
  // people to START a chat). HO sees every channel in the org — the longest
  // list of anyone — so it gets the staff app's v125.47 live filter.
  var q = String(_chatFilter || '').trim().toLowerCase();
  var visibleChannels = q ? _channels.filter(function (c) { return String(c.title || '').toLowerCase().indexOf(q) >= 0; }) : _channels;
  var items = visibleChannels.map(function (c) {
    var last = c.last_message;
    var preview = last ? ((last.sender_name ? last.sender_name + ': ' : '') + (last.deleted ? 'message removed' : last.body)) : 'No messages yet';
    var icon = c.kind === 'company' ? '📢' : c.kind === 'ho' ? '🏢' : c.kind === 'area' ? '🗺️' : c.kind === 'country' ? '🌍' : c.kind === 'dm' ? initials(c.title) : '🏪';
    return '<div class="wac-item" onclick="ChatAdmin.openChannel(\'' + jsArg(c.key) + '\',\'' + jsArg(c.title) + '\')">' +
      '<div class="wac-iav">' + icon + '</div>' +
      '<div style="flex:1;min-width:0"><div class="wac-ititle">' + esc(c.title) + '</div><div class="wac-ipreview"' + (last ? ' translate="no"' : '') + '>' + esc(preview) + '</div></div>' +
      '<div style="display:flex;flex-direction:column;align-items:flex-end;gap:4px">' +
      (last ? '<div style="font-size:11px;color:var(--adm-chat-meta)">' + esc(fmtTime(last.created_at)) + '</div>' : '') +
      (c.muted ? '<div style="font-size:11px;color:var(--adm-chat-faint)">🔕</div>' : '') +   // v125.77
      (c.unread > 0 ? '<div class="wac-iunread">' + (c.unread > 99 ? '99+' : c.unread) + '</div>' : '') + '</div></div>';
  }).join('');
  el.innerHTML = '<div class="wac-shell"><div class="wac-wp"></div>' +
    '<div class="wac-head"><div class="wac-av">💬</div>' +
    '<div><div class="wac-hname">Messages</div><div class="wac-hsub">' + (_isHO ? 'Head Office — all channels' : 'Your area') + '</div></div>' +
    (_isHO ? '<button class="wac-ai" onclick="ChatAdmin.openSummary()" title="AI summary of recent chat">✨ Summary</button>' : '') +
    '</div>' +
    flagBannerHtml() +
    '<div style="padding:8px 10px;background:var(--adm-chat-card);border-bottom:1px solid var(--adm-chat-line);z-index:1;position:relative"><input id="wacChatSearch" type="text" placeholder="🔍 Search chats" value="' + esc(_chatFilter) + '" oninput="ChatAdmin.filterChats(this.value)" style="width:100%;box-sizing:border-box;border:none;background:var(--adm-chat-panel);border-radius:18px;padding:8px 14px;font-size:13px;font-family:inherit;outline:none"></div>' +
    '<div class="wac-list">' + (items || (q ? '<div style="padding:24px;color:var(--adm-chat-sub);font-size:13px;text-align:center">No chats match "' + esc(_chatFilter) + '"</div>' : '<div style="padding:24px;color:var(--adm-chat-sub);font-size:13px;text-align:center">Loading…</div>')) + '</div>' +
    '<button class="wac-fab" onclick="ChatAdmin.openNewChat()" title="New conversation">✏️</button></div>';
}

// v125.92 — live conversation search (the staff app's v125.47 pattern verbatim:
// the repaint would blur the input mid-typing, so focus + caret are restored).
function filterChats(v) {
  _chatFilter = String(v || '');
  renderList();
  var inp = document.getElementById('wacChatSearch');
  if (inp) { inp.focus(); try { inp.setSelectionRange(inp.value.length, inp.value.length); } catch (e) {} }
}

// v125.63 (Matt: "i also need the ability to message any user") — start a NEW
// conversation with any store or any Head Office / Area Manager. Mirrors the
// staff app's new-chat picker; send() already routes _pendingDm as dm_with.
async function openNewChat() {
  _pickOpen = true; _activeKey = null; _pendingDm = null; _flagsOpen = false;
  var el = root(); if (!el) return;
  el.innerHTML = '<div class="wac-shell"><div class="wac-wp"></div>' +
    '<div class="wac-head"><button class="wac-back" onclick="ChatAdmin.backToList()">←</button>' +
    '<div class="wac-av">✏️</div><div><div class="wac-hname">New conversation</div><div class="wac-hsub">Message a store or a person</div></div></div>' +
    '<input class="wac-search" placeholder="Search stores or people…" oninput="ChatAdmin.filterNewChat(this.value)" autofocus>' +
    '<div class="wac-list" id="wacPick"><div style="padding:20px;color:var(--adm-chat-sub);font-size:13px;text-align:center">Loading…</div></div></div>';
  var stores = [], people = [];
  try { var rs = await api('/api/customer-stores'); var ds = await rs.json(); stores = (ds && ds.stores) ? ds.stores.map(function (s) { return s.store; }) : []; } catch (e) {}
  try { var ra = await api('/api/accounts'); var da = await ra.json(); people = (Array.isArray(da) ? da : []).filter(function (a) { return a && a.active !== false && a.name !== 'Head Office'; }); } catch (e) {}
  var myId = myAccountId();
  _pickPeople = people.filter(function (a) { return myId == null || Number(a.id) !== Number(myId); });
  _pickStores = stores;
  renderPickRows('');
}
// v125.65 (Matt: "how do i message individual stores... we should also be able
// to search for the store we want") — render the picker rows, filtered by a
// case-insensitive substring on the store/person name.
function renderPickRows(q) {
  var pane = document.getElementById('wacPick'); if (!pane) return;
  var needle = String(q || '').trim().toLowerCase();
  var match = function (name) { return !needle || String(name || '').toLowerCase().indexOf(needle) !== -1; };
  var hdr = function (t) { return '<div style="padding:9px 14px 5px;font-size:11px;font-weight:700;color:var(--adm-chat-faint);text-transform:uppercase;letter-spacing:.04em">' + t + '</div>'; };
  var peopleRows = _pickPeople.filter(function (a) { return match(a.name); }).map(function (a) {
    return '<div class="wac-item" onclick="ChatAdmin.startDm(\'acct:' + a.id + '\',\'' + esc(String(a.name || '').replace(/'/g, '')) + '\')">' +
      '<div class="wac-iav" data-av-kind="account" data-av-id="' + a.id + '" style="overflow:hidden">' + esc(initials(a.name)) + '</div><div style="flex:1;min-width:0"><div class="wac-ititle" translate="no">' + esc(a.name) + '</div>' +
      '<div class="wac-ipreview">' + (String(a.role || '').toLowerCase() === 'area_manager' ? 'Area Manager' : 'Head Office') + '</div></div></div>';
  }).join('');
  var storeRows = _pickStores.filter(function (s) { return match(s); }).map(function (s) {
    return '<div class="wac-item" onclick="ChatAdmin.startDm(\'store:' + jsArg(s) + '\',\'' + jsArg(s) + '\')">' +
      '<div class="wac-iav">🏪</div><div style="flex:1;min-width:0"><div class="wac-ititle">' + esc(s) + '</div></div></div>';
  }).join('');
  pane.innerHTML = (peopleRows ? hdr('Head Office &amp; Area Managers') + peopleRows : '') +
    (storeRows ? hdr('Stores') + storeRows : '') +
    (!peopleRows && !storeRows ? '<div style="padding:20px;color:var(--adm-chat-sub);font-size:13px;text-align:center">' + (needle ? 'No match for "' + esc(q) + '".' : 'Nobody to message yet.') + '</div>' : '');
}
function filterNewChat(q) { if (_pickOpen) renderPickRows(q); }
function startDm(participant, title) {
  _pickOpen = false; _pendingDm = participant; _activeKey = null; _activeTitle = title; renderThread();
}

// The "⚠️ Needs attention" banner above the channel list. When the scan found
// something, the orange banner opens the flags panel. When it found NOTHING,
// v125.87 shows a quiet all-clear line instead of vanishing — an invisible
// watcher is indistinguishable from a broken one (Matt, 2026-07-15: "have we
// lost the function…? I cant see any"). Null counts (endpoint failed / older
// Worker) still render nothing — we never fake a scan that didn't run.
function flagBannerHtml() {
  var fc = _flagCounts;
  if (!fc) return '';
  if (!fc.total) {
    return '<div style="padding:7px 14px;margin:8px;font-size:11.5px;color:var(--adm-chat-meta);background:var(--surface-2);border-radius:10px;z-index:1;position:relative">' +
      '🚩 Chat watch: all clear — no staff questions, other-language or concern messages in the last ' + (_flagDays || 14) + ' days' +
      (_flagScanned != null ? ' (' + _flagScanned + ' scanned)' : '') + '</div>';
  }
  var pill = function (cls, icon, n) { return n > 0 ? '<span class="wac-fp ' + cls + '">' + icon + ' ' + n + '</span>' : ''; };
  return '<div class="wac-flagbar" onclick="ChatAdmin.openFlags()">' +
    '<span style="font-weight:800;font-size:13px;color:var(--adm-chat-flag-text)">⚠️ Needs attention</span>' +
    '<span class="wac-flagpills">' +
      pill('wac-fp-concern', '⚠️', fc.concern) +
      pill('wac-fp-q', '❓', fc.question) +
      pill('wac-fp-lang', '🌐', fc.nonEnglish) +
    '</span><span style="margin-inline-start:auto;font-size:18px;color:var(--adm-chat-flag-text)">›</span></div>';
}

function fmtWhen(iso) {
  var d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) + ' ' + fmtTime(iso);
}

function flagRowHtml(f) {
  var store = f.sender_store ? String(f.sender_store).replace(/^[^-]+-\s*/, '') : '';
  var who = esc(f.sender_name || 'Someone') + (store ? ' · ' + esc(store) : '');
  var fl = f.flags || {};
  var tags = (fl.concern ? '<span class="wac-fp wac-fp-concern">⚠️ concern</span>' : '') +
    (fl.question ? '<span class="wac-fp wac-fp-q">❓ question</span>' : '') +
    (fl.nonEnglish ? '<span class="wac-fp wac-fp-lang">🌐 not English</span>' : '');
  var terms = (f.concern_terms && f.concern_terms.length)
    ? '<div class="wac-flag-terms">flagged on: <span translate="no">' + esc(f.concern_terms.join(', ')) + '</span></div>' : '';
  return '<div class="wac-flag-item" onclick="ChatAdmin.openFlagged(\'' + jsArg(f.channel_key) + '\',\'' + jsArg(f.channel_title) + '\')">' +
    '<div class="wac-flag-top"><span class="wac-flag-who" translate="no">' + who + '</span><span class="wac-flag-when">' + esc(fmtWhen(f.created_at)) + '</span></div>' +
    '<div class="wac-flag-body" translate="no">' + esc(f.body) + '</div>' +
    '<div class="wac-flag-tags">' + tags + '<span class="wac-flag-chan">' + esc(f.channel_title) + ' ›</span></div>' + terms +
    // v155 — clear an actioned item. stopPropagation so it doesn't also open the
    // conversation (the row's own onclick).
    '<div style="text-align:end;margin-top:8px"><button onclick="event.stopPropagation();ChatAdmin.dismissFlag(' + Number(f.id) + ')" style="border:1px solid var(--adm-chat-edge);background:var(--adm-chat-card);color:var(--adm-chat-accent);font-weight:700;font-size:12px;padding:5px 14px;border-radius:14px;cursor:pointer;font-family:inherit">✓ Done</button></div>' +
    '</div>';
}
// v155 (Matt: "Needs attention doesn't clear once actioned") — mark a flagged
// message handled; it drops out of the list for everyone.
async function dismissFlag(id) {
  try {
    var res = await api('/api/chat/flags/dismiss', { method: 'POST', body: JSON.stringify({ message_id: id }) });
    var d = await res.json().catch(function () { return {}; });
    if (res.ok && d && d.ok) {
      _flags = (_flags || []).filter(function (f) { return Number(f.id) !== Number(id); });
      _flagCounts = { question: 0, nonEnglish: 0, concern: 0, total: 0 };
      _flags.forEach(function (f) {
        var fl = f.flags || {};
        if (fl.question) _flagCounts.question += 1;
        if (fl.nonEnglish) _flagCounts.nonEnglish += 1;
        if (fl.concern) _flagCounts.concern += 1;
        _flagCounts.total += 1;
      });
      renderFlags();
    } else if (typeof showToast === 'function') { showToast((d && d.error) || 'Could not clear it'); }
  } catch (e) { if (typeof showToast === 'function') showToast('Could not clear it — check the connection'); }
}

function renderFlags() {
  var el = root(); if (!el) return;
  var list = _flags || [];
  var body = list.length
    ? list.map(flagRowHtml).join('')
    : '<div style="padding:28px;color:var(--adm-chat-sub);font-size:13px;text-align:center">Nothing needs attention right now.<br>Staff questions, non-English messages and possible concerns will appear here.</div>';
  el.innerHTML = '<div class="wac-shell"><div class="wac-wp"></div>' +
    '<div class="wac-head"><button class="wac-back" onclick="ChatAdmin.closeFlags()">←</button>' +
    '<div class="wac-av">⚠️</div>' +
    '<div><div class="wac-hname">Needs attention</div><div class="wac-hsub">Staff questions · non-English · possible concerns</div></div></div>' +
    '<div class="wac-flag-note">A heads-up list, not a verdict — tap any message to open its conversation and reply. Non-English detection covers other alphabets (Arabic, Hindi, Malayalam…); messages typed in English letters are not detected.</div>' +
    '<div class="wac-list">' + body + '</div></div>';
}

// ── v125.70 — on-demand AI summary (Matt: "admin should also have an ai
// summary"; his call: a button, not a schedule — each click is ONE bounded
// model call, aggregate themes only, no individuals named). ──────────────────
function openSummary() {
  _sumOpen = true; _flagsOpen = false; _pickOpen = false; _activeKey = null; _pendingDm = null;
  renderSummary();
  if (!_sumText && !_sumBusy) fetchSummary();
}
function closeSummary() { _sumOpen = false; renderList(); refreshChannels(); }
function refreshSummary() { if (_sumBusy) return; _sumText = null; _sumErr = null; renderSummary(); fetchSummary(); }
async function fetchSummary() {
  _sumBusy = true; _sumErr = null;
  try {
    var res = await api('/api/admin/chat-summary', { method: 'POST', body: JSON.stringify({ days: 7 }) });
    var d = await res.json().catch(function () { return {}; });
    if (res.ok && d && d.ok) { _sumText = d.text || ''; _sumStats = d.stats || null; }
    else { _sumErr = (d && d.error) || 'Could not build the summary.'; }
  } catch (e) { _sumErr = 'Could not build the summary — check your connection.'; }
  _sumBusy = false;
  if (_sumOpen) renderSummary();
}
function renderSummary() {
  var el = root(); if (!el) return;
  var body;
  if (_sumBusy || (!_sumText && !_sumErr)) {
    body = '<div style="padding:28px;color:var(--adm-chat-sub);font-size:13px;text-align:center">Reading the last 7 days of chat…<br>One moment.</div>';
  } else if (_sumErr) {
    body = '<div style="padding:28px;color:var(--adm-chat-err);font-size:13px;text-align:center">' + esc(_sumErr) + '</div>';
  } else {
    var statLine = _sumStats
      ? '<div style="padding:0 18px 10px;color:var(--adm-chat-sub);font-size:12px">' + _sumStats.total + ' messages · ' +
        _sumStats.byKind.store + ' store · ' + _sumStats.byKind.dm + ' direct · ' + _sumStats.byKind.company + ' company</div>'
      : '';
    body = '<div class="wac-sum" translate="no">' + esc(_sumText) + '</div>' + statLine +
      '<div style="text-align:center;padding-bottom:14px"><button class="wac-fbtn" onclick="ChatAdmin.refreshSummary()">↻ Refresh (one AI call)</button></div>';
  }
  el.innerHTML = '<div class="wac-shell"><div class="wac-wp"></div>' +
    '<div class="wac-head"><button class="wac-back" onclick="ChatAdmin.closeSummary()">←</button>' +
    '<div class="wac-av">✨</div>' +
    '<div><div class="wac-hname">AI summary</div><div class="wac-hsub">Last 7 days · aggregate themes only</div></div></div>' +
    '<div class="wac-flag-note">Themes, open questions and possible concerns from recent chat — no individuals are named. Use ⚠️ Needs attention for specific messages.</div>' +
    '<div class="wac-list">' + body + '</div></div>';
}

function openFlags() { _flagsOpen = true; _sumOpen = false; _pickOpen = false; _activeKey = null; _pendingDm = null; renderFlags(); }
function closeFlags() { _flagsOpen = false; renderList(); refreshChannels(); refreshFlags(); }
function openFlagged(key, title) {
  // v125.67: a non-admin HO user (ho_staff) sees the flagged MESSAGE in the row
  // itself, but isn't in the channel — opening the thread would just 403. Only
  // navigate when the viewer can actually read it (admin, or a listed channel).
  var canOpen = _isHO || _channels.some(function (c) { return c && c.key === key; });
  if (!canOpen) return;
  _flagsOpen = false; openChannel(key, title);
}

function openChannel(key, title) { _threadSig = '';   // v129.133 — a stale sig must never suppress the next thread's first paint
  _activeKey = key; _activeTitle = title; _pendingDm = null; _pickOpen = false; _sumOpen = false; _replyTo = null; _pendingPhoto = null; _pendingDoc = null; renderThread(); }
function backToList() { _activeKey = null; _pendingDm = null; _pickOpen = false; _flagsOpen = false; _sumOpen = false; _replyTo = null; _pendingPhoto = null; _pendingDoc = null; renderList(); refreshChannels(); }

// v125.55 — emoji picker (Matt: "can there [be] new emojis in Chat"). Same
// self-contained picker as the staff chat; inserts at the caret in #wacText.
var CHAT_EMOJIS = ['😀','😁','😂','🤣','😊','😍','😘','😎','🤗','🤔','😅','🙂','😉','😴','😢','😭','😡','🥳','😇','🤩','👍','👎','👌','🙏','👏','🙌','💪','🤝','👋','🔥','✅','❌','⭐','🎉','🎊','❤️','💚','💯','👀','💬','📌','⏰','☕','🛒','🧸','🎈','📦','🚚','🏪','⚠️','📷','🎁','☀️','🌙'];
function emojiPopHtml() {
  return '<div id="wacEmoji" class="wac-emoji-pop" style="display:none">' +
    CHAT_EMOJIS.map(function (e) { return '<button type="button" onclick="ChatAdmin.insertEmoji(\'' + e + '\')">' + e + '</button>'; }).join('') +
    '</div>';
}
function toggleEmoji(ev) {
  if (ev && ev.stopPropagation) ev.stopPropagation();
  var p = document.getElementById('wacEmoji'); if (!p) return;
  p.style.display = p.style.display === 'none' ? 'flex' : 'none';
}
function insertEmoji(e) {
  var ta = document.getElementById('wacText'); if (!ta) return;
  var s = ta.selectionStart != null ? ta.selectionStart : ta.value.length;
  var en = ta.selectionEnd != null ? ta.selectionEnd : ta.value.length;
  ta.value = ta.value.slice(0, s) + e + ta.value.slice(en);
  var pos = s + e.length;
  ta.focus(); try { ta.setSelectionRange(pos, pos); } catch (x) {}
  var p = document.getElementById('wacEmoji'); if (p) p.style.display = 'none';
}

function myAccountId() {
  return (typeof currentAdmin !== 'undefined' && currentAdmin && currentAdmin.id != null) ? Number(currentAdmin.id) : null;
}

function renderMessages(messages) {
  var out = [], lastDay = '';
  // v126.35: when HO OBSERVES a 1-1 chat it isn't part of (no message is "mine"),
  // colour the two participants two-tone like WhatsApp — the 2nd distinct sender
  // goes right/green, the 1st stays left/white (the ops manager's request). Group chats
  // (3+ distinct senders) stay single-tone with names, as before.
  var _skey = function (m) { return (m.sender_kind || '') + ':' + (m.sender_id != null ? m.sender_id : ('n:' + (m.sender_name || ''))); };
  var _myId = myAccountId();
  var _anyMine = false, _others = [];
  (messages || []).forEach(function (m) {
    if (m.deleted) return;
    if (m.sender_kind === 'account' && _myId != null && Number(m.sender_id) === _myId) { _anyMine = true; return; }
    var k = _skey(m);
    if (_others.indexOf(k) === -1) _others.push(k);
  });
  // v126.56 — when HO observes a DM it is not part of, put the ACCOUNT (the
  // Head Office person) on the right/green and the store/staff on the left.
  // The old rule took the 2nd distinct sender by SPEAKING ORDER, so whoever
  // happened to reply second got the green side — in a Head-Office <-> store DM
  // that is usually the store, which reads backwards to the HO person reading
  // it (Matt: "green and white messages inverted"). Order is arbitrary; the
  // participants' roles are not. Same-kind DMs (store<->store, HO<->HO) have no
  // meaningful role split, so they keep the original 2nd-sender behaviour.
  var _acctOther = null;
  _others.forEach(function (k) { if (_acctOther === null && k.indexOf('account:') === 0) _acctOther = k; });
  var _rhsKey = (!_anyMine && _others.length === 2) ? (_acctOther || _others[1]) : null;
  (messages || []).forEach(function (m) {
    var d = new Date(m.created_at);
    var dk = isNaN(d.getTime()) ? '' : d.toDateString();
    if (dk && dk !== lastDay) { out.push('<div class="wac-datechip"><span>' + esc(d.toLocaleDateString()) + '</span></div>'); lastDay = dk; }
    if (m.deleted) {
      out.push('<div class="wac-row"><div class="wac-bubble"><div class="wac-del">🚫 Message removed</div><div class="wac-btime">' + esc(fmtTime(m.created_at)) + '</div></div></div>');
      return;
    }
    var mine = m.sender_kind === 'account' && myAccountId() != null && Number(m.sender_id) === myAccountId();
    var rhs = !mine && _rhsKey && _skey(m) === _rhsKey;   // observed DM: the account side → right/green
    var canZap = m.id && (_isHO || mine);   // A6-2: HO removes any; you remove your own
    var canEdit = m.id && mine && (Date.now() - Date.parse(m.created_at)) < 15 * 60 * 1000;
    // v125.72: every message carries ↩ Reply; own/HO keep 🗑 + ✏️ (offsets stack right→left).
    var off = 4;
    var mkBtn = function (title, glyph, onclick) {
      var b = '<button class="wac-zap" style="right:' + off + 'px" title="' + title + '" onclick="' + onclick + '">' + glyph + '</button>';
      off += 20; return b;
    };
    var zap = '';
    if (canZap) zap += mkBtn('Remove (kept for audit)', '🗑', 'ChatAdmin.zap(' + m.id + ')');
    if (canEdit) zap += mkBtn('Edit (15 min)', '✏️', 'ChatAdmin.editMsg(' + m.id + ',this)');
    if (m.id) zap += mkBtn('Reply', '↩', 'ChatAdmin.startReply(' + m.id + ',this)');
    if (m.id) zap += mkBtn('Like', '👍', 'ChatAdmin.toggleLike(' + m.id + ')');       // v125.86
    if (m.id) zap += mkBtn('Forward', '↪', 'ChatAdmin.startForward(' + m.id + ',' + (m.has_photo ? 1 : 0) + ',' + (m.has_attachment ? 1 : 0) + ',this)');   // v125.96 (the ops manager)
    var quote = m.reply
      ? '<div class="wac-quote" translate="no"><b>' + esc(m.reply.sender || '') + '</b><span>' + esc(m.reply.snippet || '') + '</span></div>'
      : '';
    var name = m.sender_name + (m.sender_store ? ' · ' + esc(String(m.sender_store)).replace(/^[^-]+-\s*/, '') : '');
    var photo = m.has_photo
      ? '<div class="wac-photo" data-mid="' + m.id + '">' +
        (_photoCache[m.id] ? '<img src="' + okPhoto(_photoCache[m.id]) + '" onclick="ChatAdmin.viewPhoto(this.src)">' : '<div class="wac-photo-ph">📷 loading…</div>') + '</div>'
      : '';
    // v125.86: reaction pills (👍 3) — highlighted when one of them is mine.
    if (m.id) _reactCache[m.id] = m.reactions || [];   // v129.127 — the sheet reads this
    var reacts = m.id
      ? '<div class="wac-reacts" id="wacR' + m.id + '">' + (m.reactions || []).map(function (r) {
          // v129.127 — the pill OPENS the who-liked sheet; it no longer silently
          // flips your own vote. The 👍 button in the hover row is still the
          // one-tap like, so nothing lost a way to react.
          return '<span class="wac-react' + (r.mine ? ' mine' : '') + '" title="See who reacted" onclick="ChatAdmin.showReactions(' + m.id + ')">' + r.emoji + ' ' + r.count + '</span>';
        }).join('') + '</div>'
      : '';
    // v125.93: the message's document (Head Office: PDFs in chat) — fetched on demand.
    var doc = m.has_attachment
      ? '<div style="margin:2px 0 4px;font-size:13px">📎 <span onclick="ChatAdmin.openMsgAttachment(' + m.id + ')" style="color:var(--brand,#009E3D);font-weight:700;cursor:pointer;text-decoration:underline">View <span translate="no">' + esc(m.attachment_name || 'attachment') + '</span></span></div>'
      : '';
    // v129.121 — 🎤 the audio ships on TAP, not with the thread (a page of voice
    // notes as data URLs would be megabytes); the duration comes from the
    // thread metadata so the stub renders with nothing downloaded.
    var voice = m.has_voice ? voiceStubHtml(m.id, m.voice_duration, 'ChatAdmin.playVoice') : '';
    out.push('<div class="wac-row' + (mine ? ' me' : (rhs ? ' rhs' : '')) + '"><div class="wac-bubble" data-body="' + esc(m.body || '') + '" data-sender="' + esc(m.sender_name || '') + '">' + zap +
      (mine ? '' : '<div class="wac-bname" translate="no" style="display:flex;align-items:center;gap:5px">' +
        ((m.sender_kind === 'staff' || m.sender_kind === 'account') && m.id && typeof Avatars !== 'undefined' ? Avatars.chip(m.sender_kind, m.sender_id, m.sender_name, 20) : '') +
        '<span>' + esc(name) + '</span></div>') +
      quote +
      photo +
      doc +
      voice +
      (m.body ? '<div class="wac-btext" translate="no">' + esc(m.body) + '</div>' : '') +
      '<div class="wac-btime">' + esc(fmtTime(m.created_at)) + (m.edited ? ' · edited' : '') + '</div>' + reacts + '</div></div>');
  });
  return out.join('');
}

async function renderThread() {
  var el = root(); if (!el) return;
  // v125.77 — per-chat mute state feeds the 🔔/🔕 header toggle.
  var mutedNow = false;
  try { var mc = (_channels || []).find(function (c) { return c && c.key === _activeKey; }); mutedNow = !!(mc && mc.muted); } catch (e) {}
  // v129.123 (Matt: "i also cant see the video call button in the top corner") —
  // the staff app has had 📹 in the thread header since v129.35; this console
  // never did, because it never loaded the calls bundle. Same rule as there: the
  // room is derived from the CHANNEL server-side, so a call's audience is the
  // conversation's audience, and the Worker gates it with the same access check
  // that let this thread open. Hidden on a brand-new DM — no channel key yet
  // means there is nothing to derive a room from.
  var callBtn = _activeKey
    ? '<button id="wacCallBtn" title="Start a video call with this conversation" onclick="ChatAdmin.startCall()" ' +
      'style="margin-inline-start:auto;background:none;border:none;font-size:18px;cursor:pointer;flex-shrink:0">📹</button>'
    : '';
  var muteBtn = _activeKey
    ? '<button id="wacMuteBtn" title="' + (mutedNow ? 'Unmute this chat' : 'Mute this chat') + '" onclick="ChatAdmin.toggleMute()" ' +
      'style="' + (callBtn ? '' : 'margin-inline-start:auto;') + 'background:none;border:none;font-size:18px;cursor:pointer;flex-shrink:0">' + (mutedNow ? '🔕' : '🔔') + '</button>'
    : '';
  el.innerHTML = '<div class="wac-shell wac-full"><div class="wac-wp"></div>' +
    '<div class="wac-head"><button class="wac-back" onclick="ChatAdmin.backToList()">←</button>' +
    '<div class="wac-av">' + esc(initials(_activeTitle)) + '</div>' +
    '<div style="min-width:0"><div class="wac-hname">' + esc(_activeTitle) + '</div>' +
    '<div class="wac-hsub">' + (_isHO ? 'Head Office view' : 'Area view') + '</div></div>' + callBtn + muteBtn + '</div>' +
    '<div class="wac-msgs" id="wacMsgs">' + threadPlaceholderHtml() + '</div>' +
    '<div class="wac-pendbar" id="wacPendBar" style="display:none"></div>' +
    '<div class="wac-input">' + emojiPopHtml() +
    '<button class="wac-emoji" type="button" title="Emoji" onclick="ChatAdmin.toggleEmoji(event)">😊</button>' +
    // v125.93 (Head Office: "attach a PDF or document in chat") — 📎 used to open a
    // PICTURE picker; now 📷 = photo (compressed), 📎 = any allowed document.
    '<button class="wac-emoji" type="button" title="Attach a photo" onclick="document.getElementById(\'wacPhotoIn\').click()">📷</button>' +
    '<button class="wac-emoji" type="button" title="Attach a document" onclick="document.getElementById(\'wacDocIn\').click()">📎</button>' +
    // v129.121 — 🎤 voice note, the same as the staff app (it was missing here).
    (voiceSupported() ? '<button class="wac-emoji" type="button" id="wacMicBtn" title="Record a voice note (up to 90s)" onclick="ChatAdmin.startVoice()">🎤</button>' : '') +
    '<input id="wacPhotoIn" type="file" accept="image/*" style="display:none" onchange="ChatAdmin.photoPicked(this)">' +
    '<input id="wacDocIn" type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,image/*" style="display:none" onchange="ChatAdmin.docPicked(this)">' +
    '<textarea id="wacText" class="wac-text" rows="1" placeholder="Message" ' +
    'onkeydown="if(event.key===\'Enter\'&&!event.shiftKey){event.preventDefault();ChatAdmin.send();}"></textarea>' +
    '<button class="wac-send" onclick="ChatAdmin.send()">➤</button></div></div>';
  await refreshThread(true);
  api('/api/chat/read', { method: 'POST', body: JSON.stringify({ channel: _activeKey }) }).catch(function () {});
}

// v129.121 (Matt, 13 Aug: "Is there an issue with chat? its not loading") —
// the thread pane used to open on a bare "Loading…" and refreshThread would
// `return` on !res.ok and swallow every exception, so an expired admin token
// (401), a 5xx and a dead connection ALL looked identical to a slow network,
// forever. The silent retry is right — the poll comes round again — but the
// first paint must never keep claiming it is loading when the last attempt
// failed. The placeholder carries an id so a failure can speak into it, and
// only while it is still on screen: once real messages are painted, a
// transient poll failure must not wipe the thread.
function threadPlaceholderHtml() {
  return '<div id="wacLoading" style="padding:14px;color:var(--adm-chat-sub);font-size:13px;text-align:center;z-index:1;position:relative">Loading…</div>';
}

function threadLoadFailed(why) {
  var ph = document.getElementById('wacLoading');
  if (!ph) return;                       // real messages are up — retry quietly
  ph.innerHTML = esc(why) +
    '<div style="margin-top:9px"><button onclick="ChatAdmin.retryThread()" ' +
    'style="border:1px solid var(--adm-chat-edge);background:var(--adm-chat-card);border-radius:14px;padding:6px 14px;font-size:12.5px;cursor:pointer;font-family:inherit">Try again</button></div>';
}

function retryThread() {
  var pane = document.getElementById('wacMsgs');
  if (pane) pane.innerHTML = threadPlaceholderHtml();
  return refreshThread(true);
}

// v129.133 — REPAINT ONLY WHEN THE CONTENT CHANGED.
//
// The staff app learned this in v129.99: an unconditional pane.innerHTML on
// every poll tick "destroyed any playing 🎥 video mid-watch". This app never got
// that fix, and voice notes made the cost obvious — tap ▶ on an Android phone,
// and within 15 seconds the poll rebuilt the thread and threw the <audio>
// element away mid-download. On a store's connection the fetch often had not
// finished, so the note never played at all: "audio doesn't load on android".
//
// Same shape as the staff app, deliberately: signature the payload, and on an
// unchanged thread do nothing except re-run photo hydration (the v129.101
// retry — a fetch that resolved after its element was replaced cached the photo
// but painted nothing, and with no repaints nothing retried).
var _threadSig = '';

function threadSig(messages) {
  return _activeKey + '|' + JSON.stringify(messages || []);
}

async function refreshThread(scroll) {
  if (!_activeKey) return;
  try {
    var res = await api('/api/chat/messages?channel=' + encodeURIComponent(_activeKey));
    if (!res.ok) {
      threadLoadFailed(res.status === 401 || res.status === 403
        ? 'Your admin session has expired — sign out and sign in again to read this chat.'
        : 'Could not load the messages (error ' + res.status + ').');
      return;
    }
    var d = await res.json();
    var pane = document.getElementById('wacMsgs');
    if (pane && d && d.ok) {
      var sig = threadSig(d.messages);
      // The placeholder still being up means nothing has painted yet, so an
      // unchanged signature must NOT short-circuit the first paint.
      if (sig === _threadSig && !document.getElementById('wacLoading')) {
        hydratePhotos(pane);
        return;
      }
      _threadSig = sig;
      var atBottom = scroll || (pane.scrollHeight - pane.scrollTop - pane.clientHeight < 60);
      pane.innerHTML = renderMessages(d.messages) || '<div class="wac-datechip"><span>No messages yet</span></div>';
      if (atBottom) pane.scrollTop = pane.scrollHeight;
      hydratePhotos(pane);
    } else {
      threadLoadFailed((d && d.error) ? String(d.error) : 'Could not load the messages.');
    }
  } catch (e) {
    threadLoadFailed('No connection — the messages could not be loaded.');
  }
}

// A6: fetch each has_photo bubble's image on demand (Bearer auth) + cache.
function hydratePhotos(pane) {
  if (!pane) return;
  pane.querySelectorAll('.wac-photo[data-mid]').forEach(function (el) {
    var id = el.getAttribute('data-mid');
    if (!id || el.querySelector('img')) return;
    if (_photoCache[id]) { el.innerHTML = '<img src="' + okPhoto(_photoCache[id]) + '" onclick="ChatAdmin.viewPhoto(this.src)">'; return; }
    api('/api/chat/photo?id=' + encodeURIComponent(id))
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        if (d && d.ok && d.photo) {
          _photoCache[id] = okPhoto(d.photo);
          if (el.isConnected) el.innerHTML = '<img src="' + _photoCache[id] + '" onclick="ChatAdmin.viewPhoto(this.src)">';
        } else if (el.isConnected) { el.innerHTML = '<div class="wac-photo-ph">📷 unavailable</div>'; }
      })
      .catch(function () { if (el.isConnected) el.innerHTML = '<div class="wac-photo-ph">📷 unavailable</div>'; });
  });
}

function viewPhoto(src) {
  var v = document.createElement('div');
  v.className = 'wac-viewer';
  v.innerHTML = '<img src="' + okPhoto(src) + '">';
  v.addEventListener('click', function () { v.remove(); });
  document.body.appendChild(v);
}

// v125.58 (Matt: "if i hit submit twice it sends the message twice") — guard
// against a double-tap: _sending blocks re-entry and the button is disabled for
// the duration of the send. Mirrors the staff chat's guard, which the account
// send never had.
var _sending = false;
async function send() {
  var ta = document.getElementById('wacText');
  var text = ta ? String(ta.value || '').trim() : '';
  if ((!text && !_pendingPhoto && !_pendingDoc && !_pendingVoice) || _sending) return;   // v125.76/93/129.121: photo/doc/voice-only sends OK
  _sending = true;
  var btn = document.querySelector('.wac-send');
  if (btn) btn.disabled = true;
  // v125.75 (Matt: "quite the lag") — optimistic bubble, same as the staff app:
  // render + clear NOW, reconcile via refreshThread, un-send on any failure.
  // Photo sends keep the plain flow (their preview bar is the feedback).
  var optEl = null;
  var draft = text;
  var pane0 = document.getElementById('wacMsgs');
  if (pane0 && !_pendingPhoto && !_pendingDoc && !_pendingVoice && text) {
    optEl = document.createElement('div');
    optEl.className = 'wac-row me';
    optEl.innerHTML = '<div class="wac-bubble">' +
      (_replyTo ? '<div class="wac-quote" translate="no"><b>' + esc(_replyTo.sender || '') + '</b><span>' + esc(_replyTo.snippet || '') + '</span></div>' : '') +
      '<div class="wac-btext" translate="no">' + esc(draft) + '</div><div class="wac-btime">🕓 sending…</div></div>';
    pane0.appendChild(optEl);
    pane0.scrollTop = pane0.scrollHeight;
    if (ta) ta.value = '';
  }
  var unsend = function () {
    if (optEl) { try { optEl.remove(); } catch (e2) {} optEl = null; }
    if (ta && !ta.value) ta.value = draft;
  };
  try {
    var payload = { body: text };
    if (_pendingPhoto) payload.photo = _pendingPhoto;                    // v125.76 attach
    if (_pendingDoc) payload.attachment = _pendingDoc;                   // v125.93 document
    if (_pendingVoice) { payload.voice = _pendingVoice.dataUrl; payload.voice_duration = _pendingVoice.duration; }   // v129.121 🎤
    if (_replyTo) payload.reply_to = _replyTo.id;                        // v125.72 quote-reply
    if (_activeKey) payload.channel = _activeKey; else if (_pendingDm) payload.dm_with = _pendingDm;
    var res = await api('/api/chat/send', { method: 'POST', body: JSON.stringify(payload) });
    var d = await res.json().catch(function () { return {}; });
    if (!res.ok || d.ok === false) {
      unsend();
      if (typeof showToast === 'function') showToast((d && d.error) || 'Could not send');
      return;
    }
    if (_pendingDm && d.channel) { _activeKey = d.channel; _pendingDm = null; }
    if (ta) ta.value = '';
    // v125.93: the message saved but the FILE didn't (v160 not run) — fail loud.
    if (_pendingDoc && d.attachment_saved === false && typeof showToast === 'function') {
      showToast('Sent — but the file was NOT attached. Run migrations/v160-0-chat-message-attachments.sql on this environment.', 'error');
    }
    // v129.121: the message saved but the AUDIO didn't (v197-0 not run) — same
    // fail-loud rule as the document, naming the migration.
    if (_pendingVoice && d.voice_saved === false && typeof showToast === 'function') {
      showToast('Sent — but the voice note was NOT saved. Run migrations/v197-0-chat-voice.sql on this environment.', 'error');
    }
    _replyTo = null; _pendingPhoto = null; _pendingDoc = null; _pendingVoice = null; renderReplyBar();  // v125.72/76/93 + v129.121: reply + photo + doc + voice went with the send
    if (optEl) { var tEl = optEl.querySelector('.wac-btime'); if (tEl) tEl.textContent = fmtTime(new Date().toISOString()); }
    await refreshThread(true);
  } catch (e) {
    unsend();
    if (typeof showToast === 'function') showToast('Could not send — check the connection');
  } finally {
    _sending = false;
    var b2 = document.querySelector('.wac-send');
    if (b2) b2.disabled = false;
  }
}

// ── Photo attach (v125.76 — Matt: "i also cant see an attach option in chat?")
// The staff app had 📎 since A6; this composer never got it. Same client-side
// compression + bounded data-URL contract the Worker already accepts.
var _pendingPhoto = null;

function compressImage(file, cb) {
  var img = new Image();
  var url = URL.createObjectURL(file);
  img.onload = function () {
    try {
      var scale = Math.min(1, 1280 / Math.max(img.width, img.height));
      var canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(img.width * scale));
      canvas.height = Math.max(1, Math.round(img.height * scale));
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      var out = null;
      [0.72, 0.55, 0.4].some(function (q) {
        var d = canvas.toDataURL('image/jpeg', q);
        if (d.length <= 400000) { out = d; return true; }
        out = d; return false;
      });
      URL.revokeObjectURL(url);
      cb(out && out.length <= 400000 ? out : null);
    } catch (e) { URL.revokeObjectURL(url); cb(null); }
  };
  img.onerror = function () { URL.revokeObjectURL(url); cb(null); };
  img.src = url;
}

function photoPicked(input) {
  var file = input && input.files && input.files[0];
  if (input) input.value = '';                       // allow re-picking the same file
  if (!file) return;
  compressImage(file, function (dataUrl) {
    if (!dataUrl) {
      if (typeof showToast === 'function') showToast('Could not use that photo — try a different one.');
      return;
    }
    _pendingPhoto = dataUrl;
    renderReplyBar();
  });
}
function clearPendingPhoto() { _pendingPhoto = null; renderReplyBar(); }

// v125.100 — paste a screenshot straight into the composer (the ops manager). Mirror
// of the staff app's bindPaste: image on the clipboard + open thread → the same
// compress + pending pipeline as the 📷 picker. Text pastes untouched.
var _pasteBound = false;
function bindPaste() {
  if (_pasteBound || typeof document === 'undefined') return;
  _pasteBound = true;
  document.addEventListener('paste', function (e) {
    try {
      var pane = root();
      if (!pane || pane.offsetParent === null) return;     // Messages tab not visible
      if (!_activeKey && !_pendingDm) return;              // composer not open
      var items = (e.clipboardData && e.clipboardData.items) || [];
      for (var i = 0; i < items.length; i++) {
        if (items[i].kind === 'file' && /^image\//.test(items[i].type || '')) {
          var f = items[i].getAsFile();
          if (!f) continue;
          e.preventDefault();
          compressImage(f, function (dataUrl) {
            if (!dataUrl) {
              if (typeof showToast === 'function') showToast('Could not use that image — try a different one.');
              return;
            }
            _pendingPhoto = dataUrl;
            renderReplyBar();
            if (typeof showToast === 'function') showToast('📷 Image pasted — press send');
          });
          return;
        }
      }
    } catch (err) { /* a paste must never break typing */ }
  });
}

// ── v125.93 — document attach (Head Office: "attach a PDF or document in chat") ────
// The v154 pattern: FileReader → bounded data URL; the Worker validates
// (allow-listed types, 6MB) and stores it in chat_message_attachments (v160).
var _pendingDoc = null;
var _pendingVoice = null;   // v129.121 — { dataUrl, duration } 🎤 awaiting send
var _recording = false;     // true while the microphone is live
function chatResolveMime(f) {
  var allow = ['image/png','image/jpeg','image/jpg','image/webp','image/gif','application/pdf','application/msword','application/vnd.openxmlformats-officedocument.wordprocessingml.document','application/vnd.ms-excel','application/vnd.openxmlformats-officedocument.spreadsheetml.sheet','text/plain','text/csv'];
  var t = String((f && f.type) || '').toLowerCase();
  if (allow.indexOf(t) >= 0) return t;
  var ext = String((f && f.name) || '').split('.').pop().toLowerCase();
  var map = { png:'image/png', jpg:'image/jpeg', jpeg:'image/jpeg', webp:'image/webp', gif:'image/gif', pdf:'application/pdf', doc:'application/msword', docx:'application/vnd.openxmlformats-officedocument.wordprocessingml.document', xls:'application/vnd.ms-excel', xlsx:'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', csv:'text/csv', txt:'text/plain' };
  return map[ext] || t || 'application/octet-stream';
}
function docPicked(input) {
  var f = input && input.files && input.files[0];
  if (input) input.value = '';                       // allow re-picking the same file
  if (!f) return;
  if (f.size > 6 * 1024 * 1024) { if (typeof showToast === 'function') showToast('That file is too large (max 6 MB).', 'error'); return; }
  var reader = new FileReader();
  reader.onload = function () {
    var mime = chatResolveMime(f);
    var b64 = String(reader.result || '').split(',')[1] || '';
    _pendingDoc = { filename: f.name, content_type: mime, data: 'data:' + mime + ';base64,' + b64 };
    renderReplyBar();
  };
  reader.onerror = function () { if (typeof showToast === 'function') showToast('Could not read that file — try again.', 'error'); };
  reader.readAsDataURL(f);
}
function clearPendingDoc() { _pendingDoc = null; renderReplyBar(); }
// The message's document, fetched on demand + opened with the v125.81 iOS-safe
// opener (data:-URL anchors are silent no-ops in installed PWAs).
async function openMsgAttachment(id) {
  try {
    var res = await api('/api/chat/attachment?id=' + encodeURIComponent(id));
    if (!res.ok) { if (typeof showToast === 'function') showToast('Could not open the attachment', 'error'); return; }
    var d = await res.json();
    if (d && d.ok && d.data) openChatDataUrl(d.data, d.filename, d.content_type);
  } catch (e) { if (typeof showToast === 'function') showToast('Could not open the attachment', 'error'); }
}
function openChatDataUrl(dataUrl, filename, contentType) {
  // v129.122 — the Head Office twin of the staff copy below/above: a
  // hand-rolled opener that v129.112's sweep missed, with the same
  // media-type-parameter bug. Routed through the one shared opener.
  openStoredFile(dataUrl, filename, contentType);
}

// ── Quote-reply (v125.72) ─────────────────────────────────────────────────────
// The ops manager: "likewise in whatsapp, i want to respond to that particular line."
var _replyTo = null;   // { id, sender, snippet } while composing a reply

function startReply(id, btn) {
  var bubble = btn && btn.closest ? btn.closest('.wac-bubble') : null;
  var body = (bubble && bubble.getAttribute('data-body')) || '';
  var sender = (bubble && bubble.getAttribute('data-sender')) || '';
  _replyTo = { id: id, sender: sender, snippet: body.length > 90 ? body.slice(0, 89) + '…' : body };
  renderReplyBar();
  var ta = document.getElementById('wacText');
  if (ta) ta.focus();
}
// ── v129.123 — 📹 turn this conversation into a call ─────────────────────────
// The mirror of the staff app's Chat.startCall. Deliberately NOT shared: the
// two apps authenticate differently (this one's api() carries the admin Bearer)
// and the confirm text is the only other moving part. What IS shared is the
// rule — POST, never GET, because this RINGS the other side, and a write must
// not hang off a request a retry or a prefetch could repeat.
async function startCall() {
  if (!_activeKey) return;
  if (typeof Calls === 'undefined' || !Calls.joinFromResponse) {
    // Before v129.123 this console never loaded dist/calls.js at all. If the
    // script tag is ever dropped again, say so rather than doing nothing.
    if (typeof showToast === 'function') showToast('Calls are not available here yet — reload the page');
    return;
  }
  // v129.120 (Matt, 13 Aug): the tap rings the other till's whole screen, so the
  // etiquette line sits on the caller — the only person who can act on it.
  try {
    if (typeof window !== 'undefined' && typeof window.confirm === 'function' &&
        !window.confirm('Start a call in ' + (_activeTitle || 'this conversation') + '?\n\nMake sure the store is expecting your call — drop them a message here first if not.')) {
      return;
    }
  } catch (e) { /* no confirm available — proceed */ }
  try {
    var res = await api('/api/chat/call?channel=' + encodeURIComponent(_activeKey), { method: 'POST', body: JSON.stringify({}) });
    var d = await res.json().catch(function () { return {}; });
    if (!res.ok || !d || !d.ok || !d.room) {
      if (typeof showToast === 'function') showToast((d && d.error) || 'Could not start a call for this conversation', 'error');
      return;
    }
    // "It rang them" and "it opened but did not ring" look identical from the
    // caller's side otherwise — and the second one needs them to go and say so
    // in the thread.
    if (typeof showToast === 'function') {
      if (d.ring && d.ring.rang) showToast('📞 Ringing ' + d.ring.to + '…');
      else if (d.ring && d.ring.note) showToast(d.ring.note);
    }
    Calls.joinFromResponse(d, _activeTitle || 'Call', { kind: 'chat', target: _activeKey });
  } catch (e) {
    if (typeof showToast === 'function') showToast('Could not start the call — check the connection', 'error');
  }
}

// ── v129.121 — 🎤 voice notes (shared recorder; this app owns only its markup)
async function startVoice() {
  if (_pendingVoice) return;                     // clear the pending one first
  var ok = await startRecording({
    onTick: function (secs) {
      var el = document.getElementById('wacRecT');
      if (el) el.textContent = vnFmtDur(secs);
    },
    onDone: function (v) { _pendingVoice = v; _recording = false; renderReplyBar(); },
    onCancel: function () { _recording = false; renderReplyBar(); },
    onError: function (m) {
      _recording = false; renderReplyBar();
      if (typeof showToast !== 'undefined') showToast(m, 'error'); else alert(m);
    },
  });
  if (ok) { _recording = true; renderReplyBar(); }
}
function stopVoice() { stopRecording(); }
function cancelVoice() { cancelRecording(); }
function clearPendingVoice() { _pendingVoice = null; renderReplyBar(); }

// Play a received voice note — fetched ON TAP (Bearer auth here, unlike the
// staff app's read identity), then swapped in as a real player.
function playVoice(id, el) {
  return playVoiceInto(id, el, async function (mid) {
    var res = await api('/api/chat/voice?id=' + encodeURIComponent(mid));
    var d = res.ok ? await res.json() : null;
    return d && d.ok ? d.voice : null;
  });
}

function cancelReply() { _replyTo = null; renderReplyBar(); }
function renderReplyBar() {
  var bar = document.getElementById('wacPendBar');
  if (!bar) return;
  if (_replyTo) {
    bar.style.display = 'flex';
    bar.innerHTML = '<span style="font-size:12px;color:var(--adm-chat-sub);flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">' +
      '↩ Replying to <b translate="no">' + esc(_replyTo.sender || '') + '</b>: <span translate="no">' + esc(_replyTo.snippet || '') + '</span>' +
      (_pendingPhoto ? ' · 📷' : '') + (_pendingDoc ? ' · 📎' : '') + (_pendingVoice ? ' · 🎤' : '') + '</span>' +
      '<button class="wac-pendx" onclick="ChatAdmin.cancelReply()">✕</button>';
  } else if (_pendingPhoto) {   // v125.76 attach
    bar.style.display = 'flex';
    bar.innerHTML = '<img src="' + _pendingPhoto + '"><span style="font-size:12px;color:var(--adm-chat-sub);flex:1">Photo ready — press send</span>' +
      '<button class="wac-pendx" onclick="ChatAdmin.clearPendingPhoto()">✕</button>';
  } else if (_pendingDoc) {   // v125.93 — document ready
    bar.style.display = 'flex';
    bar.innerHTML = '<span style="font-size:12px;color:var(--adm-chat-sub);flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">📎 <span translate="no">' + esc(_pendingDoc.filename || 'file') + '</span> — press send</span>' +
      '<button class="wac-pendx" onclick="ChatAdmin.clearPendingDoc()">✕</button>';
  } else if (_recording) {    // v129.121 — recording NOW: red dot + live timer
    bar.style.display = 'flex';
    bar.innerHTML = '<span style="font-size:12px;color:var(--adm-chat-rec);font-weight:700;flex:1">● Recording… <span id="wacRecT">0:00</span> / 1:30</span>' +
      '<button class="wac-pendx" title="Discard" onclick="ChatAdmin.cancelVoice()">✕</button>' +
      '<button class="wac-pendx" title="Finish" style="color:var(--brand,#009E3D);font-weight:800" onclick="ChatAdmin.stopVoice()">✓</button>';
  } else if (_pendingVoice) { // v129.121 — recorded: LISTEN before sending
    bar.style.display = 'flex';
    bar.innerHTML = '<audio controls id="wacVoicePrev" style="height:32px;max-width:55%"></audio>' +
      '<span style="font-size:12px;color:var(--adm-chat-sub);flex:1">🎤 ' + esc(vnFmtDur(_pendingVoice.duration)) + ' — press send</span>' +
      '<button class="wac-pendx" onclick="ChatAdmin.clearPendingVoice()">✕</button>';
    try {
      // Blob + object URL, src as a PROPERTY — the v129.112 rule.
      var pv = document.getElementById('wacVoicePrev');
      var dec = decodeDataUrl(_pendingVoice.dataUrl);
      if (pv && dec) pv.src = URL.createObjectURL(new Blob([dec.bytes], { type: dec.mime }));
    } catch (e) { /* preview is best-effort — the send still works */ }
  } else {
    bar.style.display = 'none';
    bar.innerHTML = '';
  }
}

// ── Per-chat mute (v125.77) — Matt: "act like whats app". Notifications only;
// messages, badges and history keep flowing. Bearer accounts mute their phone.
async function toggleMute() {
  if (!_activeKey) return;
  var c = (_channels || []).find(function (x) { return x && x.key === _activeKey; });
  var next = !(c && c.muted);
  try {
    var res = await api('/api/chat/mute', { method: 'POST', body: JSON.stringify({ channel: _activeKey, muted: next }) });
    var d = await res.json().catch(function () { return {}; });
    if (!res.ok || d.ok === false) {
      if (typeof showToast === 'function') showToast((d && d.error) || 'Could not change mute');
      return;
    }
    if (c) c.muted = next;
    var btn = document.getElementById('wacMuteBtn');
    if (btn) { btn.textContent = next ? '🔕' : '🔔'; btn.title = next ? 'Unmute this chat' : 'Mute this chat'; }
    if (typeof showToast === 'function') showToast(next ? '🔕 Muted — no more notifications from this chat' : '🔔 Unmuted');
  } catch (e) {
    if (typeof showToast === 'function') showToast('Could not change mute — check the connection');
  }
}

// v125.86 (Matt: "like a post in chat") — toggle my 👍 on one message; the pill
// updates in place from the server's post-change count (the poll reconciles
// everyone else's). Accounts always react personally (Bearer).
// ── v129.127 — WHO liked it (the ops manager, 13 Aug: "when i click like icon it
// doesn't show who has liked it (like in what's app) … instead it add you as new
// like or reduce one if u already liked it") ─────────────────────────────────
//
// He is describing the staff app's v127.49 sheet, which Head Office never got.
// Tapping a pill here called toggleLike, so the only thing a pill could do was
// change your own vote — you could see that 5 people liked a message and had no
// way to find out who.
//
// The DATA was never the problem: /api/chat/messages has carried
// reactions[].names since v127.49 and this app already receives them. Only the
// UI was missing. Fifth both-apps gap this week, so the port is faithful rather
// than reinvented — same sheet, same quick-reaction row, this app's markup.
var _reactCache = {};            // message id → [{emoji,count,mine,names}]

// A subset of the server's REACTION_EMOJI — enough to like, love, applaud and
// celebrate without a full picker. Indexed into the onclick, never interpolated
// as text into a JS string (the injection rule, even for our own constants).
var QUICK_REACTS = ['👍', '❤️', '👏', '🎉'];

function showReactions(id) {
  var list = _reactCache[id] || [];
  if (!list.length) { toggleLike(id); return; }   // no pills yet — treat the tap as a quick like
  closeReactions();
  var sheet = document.createElement('div');
  sheet.id = 'wacReactSheet';
  sheet.style.cssText = 'position:fixed;inset:0;background:var(--overlay);z-index:99998;display:flex;align-items:flex-end;justify-content:center';
  var rows = list.map(function (r) {
    var names = r.names || [];
    // The server caps the name list; say so rather than implying it is everyone.
    var more = r.count > names.length ? '<div style="font-size:12px;color:var(--adm-chat-sub);padding:2px 0">+ ' + (r.count - names.length) + ' more</div>' : '';
    return '<div style="padding:10px 14px;border-bottom:1px solid var(--adm-chat-line)">' +
      '<div style="font-weight:800;font-size:13px;color:var(--adm-chat-text);margin-bottom:4px">' + r.emoji + ' ' + r.count + '</div>' +
      names.map(function (n) { return '<div translate="no" style="font-size:13px;color:var(--adm-chat-text);padding:2px 0">' + esc(n) + '</div>'; }).join('') + more +
      '</div>';
  }).join('');
  var quickBtns = QUICK_REACTS.map(function (em, qi) {
    var mineEm = list.some(function (r) { return r.emoji === em && r.mine; });
    return '<button onclick="ChatAdmin.quickReact(' + id + ',' + qi + ')" style="border:1px solid ' + (mineEm ? 'var(--brand,#009E3D)' : 'var(--adm-chat-edge)') + ';background:' + (mineEm ? 'var(--adm-chat-react-mine)' : 'var(--adm-chat-card)') + ';font-size:15px;cursor:pointer;border-radius:10px;padding:4px 9px">' + em + '</button>';
  }).join('');
  sheet.innerHTML = '<div style="background:var(--adm-chat-card);width:100%;max-width:440px;max-height:70vh;border-radius:14px 14px 0 0;overflow:hidden;display:flex;flex-direction:column">' +
    '<div style="display:flex;align-items:center;justify-content:space-between;padding:12px 14px;border-bottom:1px solid var(--adm-chat-line);gap:8px">' +
      '<div style="font-weight:800;font-size:14px;color:var(--adm-chat-text)">Reactions</div>' +
      '<div style="display:flex;gap:6px;align-items:center">' + quickBtns +
      '<button onclick="ChatAdmin.closeReactions()" style="border:none;background:transparent;font-size:16px;cursor:pointer;color:var(--adm-chat-sub)">✕</button></div></div>' +
    '<div style="overflow-y:auto">' + rows + '</div></div>';
  sheet.addEventListener('click', function (e) { if (e.target === sheet) closeReactions(); });
  document.body.appendChild(sheet);
}

function closeReactions() {
  var s = document.getElementById('wacReactSheet');
  if (s) s.remove();
}

function quickReact(id, qi) {
  var em = QUICK_REACTS[qi];
  if (em) { toggleLike(id, em); closeReactions(); }
}

async function toggleLike(id, emoji) {
  emoji = emoji || '👍';
  var rowEl = document.getElementById('wacR' + id);
  var pill = null;
  if (rowEl) {
    for (var i = 0; i < rowEl.children.length; i++) {
      if (rowEl.children[i].textContent.indexOf(emoji) === 0) { pill = rowEl.children[i]; break; }
    }
  }
  var had = !!(pill && pill.className.indexOf('mine') !== -1);
  try {
    var res = await api('/api/chat/react', { method: 'POST', body: JSON.stringify({ message_id: id, emoji: emoji, liked: !had }) });
    var d = await res.json().catch(function () { return {}; });
    if (!res.ok || d.ok === false) {
      if (typeof showToast === 'function') showToast((d && d.error) || 'Could not react');
      return;
    }
    if (rowEl) {
      if (d.count > 0) {
        if (!pill) {
          pill = document.createElement('span');
          // v129.127 — a pill created optimistically here must behave like a
          // rendered one: tapping it opens the who-reacted sheet, not a second
          // silent toggle. (Its names come from the last render; the 15s poll
          // refreshes them.)
          pill.onclick = function () { showReactions(id); };
          pill.title = 'See who reacted';
          rowEl.appendChild(pill);
        }
        pill.className = 'wac-react' + (d.liked ? ' mine' : '');
        pill.textContent = emoji + ' ' + d.count;
      } else if (pill) { pill.remove(); }
    }
  } catch (e) { if (typeof showToast === 'function') showToast('Could not react — check the connection'); }
}

// ── v125.96 — Forward (the ops manager: "forward an Image from Comms to another
// user … i expect a drill down button to choose, like reply button").
// WhatsApp-style: ↪ → pick a destination from your channel list → the text /
// photo / document is re-sent with an '↪ Forwarded' marker. All existing
// endpoints — the server still decides who may post where. Mirrors src/chat.
var _forward = null;

function startForward(id, hasPhoto, hasAtt, btn) {
  var bubble = btn && btn.closest ? btn.closest('.wac-bubble') : null;
  var body = (bubble && bubble.getAttribute('data-body')) || '';
  _forward = { id: id, body: body, hasPhoto: !!hasPhoto, hasAtt: !!hasAtt };
  var old = document.getElementById('wacFwdSheet'); if (old) old.remove();
  var sheet = document.createElement('div');
  sheet.id = 'wacFwdSheet';
  sheet.style.cssText = 'position:fixed;inset:0;background:var(--overlay);z-index:99998;display:flex;align-items:flex-end;justify-content:center';
  var items = (_channels || []).map(function (c) {
    var k = jsArg(c.key);
    var icon = c.kind === 'company' ? '📢' : c.kind === 'ho' ? '🏢' : c.kind === 'area' ? '🗺️' : c.kind === 'country' ? '🌍' : c.kind === 'dm' ? '👤' : '🏪';
    return '<div onclick="ChatAdmin.forwardTo(\'' + k + '\')" style="display:flex;align-items:center;gap:10px;padding:11px 14px;border-bottom:1px solid var(--adm-chat-line);cursor:pointer;background:var(--adm-chat-card)">' +
      '<span style="font-size:18px">' + icon + '</span>' +
      '<span style="font-size:13.5px;color:var(--adm-chat-text);font-weight:600">' + esc(c.title || c.key) + '</span></div>';
  }).join('');
  sheet.innerHTML = '<div style="background:var(--adm-chat-card);width:100%;max-width:440px;max-height:70vh;border-radius:14px 14px 0 0;overflow:hidden;display:flex;flex-direction:column">' +
    '<div style="display:flex;align-items:center;justify-content:space-between;padding:12px 14px;border-bottom:1px solid var(--adm-chat-line)">' +
      '<div style="font-weight:800;font-size:14px;color:var(--adm-chat-text)">↪ Forward to…</div>' +
      '<button onclick="ChatAdmin.closeForward()" style="border:none;background:transparent;font-size:16px;cursor:pointer;color:var(--adm-chat-sub)">✕</button></div>' +
    '<div style="overflow-y:auto">' + (items || '<div style="padding:20px;color:var(--adm-chat-sub);font-size:13px;text-align:center">No conversations yet</div>') + '</div></div>';
  sheet.addEventListener('click', function (e) { if (e.target === sheet) closeForward(); });
  document.body.appendChild(sheet);
}

function closeForward() {
  _forward = null;
  var s = document.getElementById('wacFwdSheet');
  if (s) s.remove();
}

async function forwardTo(destKey) {
  var fwd = _forward;
  closeForward();
  if (!fwd) return;
  if (typeof showToast === 'function') showToast('↪ Forwarding…');
  try {
    var payload = { channel: destKey, body: fwd.body ? '↪ Forwarded:\n' + fwd.body : '' };
    if (fwd.hasPhoto) {
      var pr = await api('/api/chat/photo?id=' + encodeURIComponent(fwd.id));
      var pd = pr.ok ? await pr.json() : null;
      if (pd && pd.photo) payload.photo = pd.photo;
    }
    if (fwd.hasAtt) {
      var ar = await api('/api/chat/attachment?id=' + encodeURIComponent(fwd.id));
      var ad = ar.ok ? await ar.json() : null;
      if (ad && ad.data) payload.attachment = { filename: ad.filename, content_type: ad.content_type, data: ad.data };
    }
    if (!payload.body && !payload.photo && !payload.attachment) {
      if (typeof showToast === 'function') showToast('Nothing to forward from that message', 'error');
      return;
    }
    var res = await api('/api/chat/send', { method: 'POST', body: JSON.stringify(payload) });
    var d = await res.json().catch(function () { return {}; });
    if (!res.ok || d.ok === false) {
      if (typeof showToast === 'function') showToast((d && d.error) || 'Could not forward', 'error');
      return;
    }
    if (typeof showToast === 'function') showToast('↪ Forwarded');
    if (destKey === _activeKey) { await refreshThread(true); }
  } catch (e) {
    if (typeof showToast === 'function') showToast('Could not forward — check the connection');
  }
}

// A6-2: edit your own message within 15 min (prompt keeps the admin flow light).
async function editMsg(id, btn) {
  var bubble = btn && btn.closest ? btn.closest('.wac-bubble') : null;
  var current = (bubble && bubble.getAttribute('data-body')) || '';
  var next = prompt('Edit message:', current);
  if (next == null || next === current) return;
  try {
    var res = await api('/api/chat/edit', { method: 'POST', body: JSON.stringify({ id: id, body: next }) });
    var d = await res.json().catch(function () { return {}; });
    if (!res.ok && typeof showToast === 'function') showToast((d && d.error) || 'Could not edit');
  } catch (e) { /* refresh shows the truth */ }
  refreshThread();
}

// HO moderation: SOFT delete (the row is retained for audit; readers see a stub).
async function zap(id) {
  if (!confirm('Remove this message for everyone? It stays on record for audit.')) return;
  try {
    var res = await api('/api/chat/delete', { method: 'POST', body: JSON.stringify({ id: id }) });
    if (!res.ok && typeof showToast === 'function') showToast('Could not remove (Head Office only)');
  } catch (e) { /* refresh shows the truth */ }
  refreshThread();
}

if (typeof window !== 'undefined') {
  window.renderChatAdmin = render;
}

export { render, openChannel, backToList, send, zap, editMsg, viewPhoto, renderMessages, esc, initials,
  openFlags, closeFlags, openFlagged, refreshFlags, dismissFlag, toggleEmoji, insertEmoji,
  openNewChat, startDm, filterNewChat, filterChats,
  openSummary, closeSummary, refreshSummary,
  startReply, cancelReply,
  photoPicked, clearPendingPhoto, toggleMute, toggleLike,
  docPicked, clearPendingDoc, openMsgAttachment,      // v125.93 — documents in chat
  startVoice, stopVoice, cancelVoice, clearPendingVoice, playVoice,   // v129.121 — 🎤 voice notes
  retryThread,                                        // v129.121 — the stuck-"Loading…" escape hatch
  startCall,                                          // v129.123 — 📹 in the Head Office thread header
  showReactions, closeReactions, quickReact,          // v129.127 — who reacted (the ops manager)
  startForward, forwardTo, closeForward };            // v125.96 — WhatsApp-style forward
